import { useCallback, useEffect, useMemo } from 'react';

import { ChecklistCategory } from '../../../types';
import { useAtom } from 'jotai';
import {
  activeChecklistCategoryState,
  sortedChecklistCategoriesState,
} from '../../../state/UIState';
import {
  useChecklistCategories,
  useCreateChecklistCategory,
  useUpdateChecklistCategories,
  useUpdateChecklistCategory,
} from '../../../queries/useChecklistCategories';
import { useAppConfig, useUpdateAppConfig } from '../../../queries/useAppConfig';

export function useSortedChecklistCategories() {
  const { data: appConfig, isLoading: isConfigLoading } = useAppConfig();
  const { data: checklistCategories, isLoading: areCategoriesLoading } = useChecklistCategories();
  const [sortedItems, setSortedItems] = useAtom(sortedChecklistCategoriesState);
  const [, setActiveCategory] = useAtom(activeChecklistCategoryState);
  const updatePageConfig = useUpdateAppConfig();
  const updateChecklistCategories = useUpdateChecklistCategories();
  const createChecklistCategory = useCreateChecklistCategory();
  const updateChecklistCategory = useUpdateChecklistCategory();
  useEffect(() => {
    if (!checklistCategories || !appConfig || isConfigLoading || areCategoriesLoading) {
      return;
    }
    setSortedItems(checklistCategories);

    setActiveCategory((prev) => {
      const prevActive = checklistCategories?.find((category) => category.id === prev?.id);
      return prevActive || checklistCategories[0];
    });
  }, [
    setSortedItems,
    areCategoriesLoading,
    setActiveCategory,
    checklistCategories,
    appConfig,
    isConfigLoading,
  ]);

  const onReorder = useCallback(
    async (newOrder: ChecklistCategory[]) => {
      if (!appConfig?.data) {
        return;
      }
      const oldOrder = [...sortedItems];
      setSortedItems(newOrder);
      const payload = newOrder.map((item) => ({
        id: item.id,
        order: item.order,
      }));
      await updateChecklistCategories.mutateAsync(
        {
          data: payload,
        },
        {
          onError: () => {
            setSortedItems(oldOrder);
          },
        }
      );
      await updatePageConfig.mutateAsync({
        data: {
          ...appConfig?.data,
          areCheckListSurveysUpToDate: false,
        },
      });
    },
    [appConfig?.data, setSortedItems, sortedItems, updateChecklistCategories, updatePageConfig]
  );

  const onAddCategory = useCallback(async () => {
    if (!appConfig?.data) {
      return;
    }
    const newCategory = await createChecklistCategory.mutateAsync({
      name: 'New Category',
      order: sortedItems.length + 1,
    });
    const newOrder = [...sortedItems, { ...newCategory, isNew: true }];
    const payload = newOrder.map((item) => ({
      id: item.id,
      order: item.order,
    }));

    await updateChecklistCategories.mutateAsync(
      {
        data: payload,
      },
      {
        onSuccess: () => {
          setSortedItems(newOrder);
          setActiveCategory(newCategory);
        },
      }
    );
    await updatePageConfig.mutateAsync({
      data: {
        ...appConfig?.data,
        areCheckListSurveysUpToDate: false,
      },
    });
  }, [
    appConfig?.data,
    createChecklistCategory,
    setActiveCategory,
    setSortedItems,
    sortedItems,
    updateChecklistCategories,
    updatePageConfig,
  ]);

  const onRemoveCategory = useCallback(
    async (id: number) => {
      if (!appConfig?.data) {
        return;
      }
      const oldOrder = [...sortedItems];
      const newOrder = sortedItems.filter((item) => item.id !== id);
      await updateChecklistCategory.mutateAsync({ id, isDeleted: true });
      const payload = newOrder.map((item, i) => ({
        id: item.id,
        order: i + 1,
      }));
      await updateChecklistCategories.mutateAsync(
        {
          data: payload,
        },
        {
          onError: () => {
            setSortedItems(oldOrder);
          },
          onSuccess: () => {
            setActiveCategory(newOrder[0] ? newOrder[0] : null);
          },
        }
      );
      await updatePageConfig.mutateAsync({
        data: {
          ...appConfig?.data,
          areCheckListSurveysUpToDate: false,
        },
      });
    },
    [
      appConfig?.data,
      setActiveCategory,
      setSortedItems,
      sortedItems,
      updateChecklistCategories,
      updateChecklistCategory,
      updatePageConfig,
    ]
  );

  const onRenameCategory = useCallback(
    async (id: number, name: string) => {
      await updateChecklistCategory.mutateAsync(
        { id, name },
        {
          onSuccess: async () => {
            if (!appConfig?.data) {
              return;
            }
            await updatePageConfig.mutateAsync({
              data: {
                ...appConfig?.data,
                areCheckListSurveysUpToDate: false,
              },
            });
          },
        }
      );
    },
    [appConfig?.data, updateChecklistCategory, updatePageConfig]
  );

  const value = useMemo(() => {
    return {
      items: sortedItems as ChecklistCategory[],
      isLoading: isConfigLoading || areCategoriesLoading,
      onReorder,
      onAddCategory,
      onRemoveCategory,
      onRenameCategory,
    };
  }, [
    areCategoriesLoading,
    isConfigLoading,
    onAddCategory,
    onRemoveCategory,
    onReorder,
    sortedItems,
    onRenameCategory,
  ]);

  return value;
}
